import { AxiosError, AxiosResponse } from 'axios';

import { Dispatch } from '@reduxjs/toolkit';
import { AXIOS_STATUS, configService, dayjs, FORMAT_DATE_API, UUID } from '@umanforge/common';
import { User } from 'interfaces/user.interface';
import { AxiosService, CommonGlobalService, CallbackWarningAxios, CallbackErrorAxios } from '@umanforge/common';
import { ToastService } from 'services/toast.service';
import { EventCommitment } from 'interfaces/types.interface';
import { Dayjs } from 'dayjs';

export class GlobalService {
  static toastService: ToastService = new ToastService( 'App' );

  static onAxiosWarning( response: AxiosResponse, onWarning?: CallbackWarningAxios ): void {
    this.toastService.showWarning( AxiosService.getAxiosMessage( response ) );
    if ( onWarning ) {
      onWarning( response );
    }
  }

  static onAxiosError( error: AxiosError, dispatch: Dispatch, onError?: CallbackErrorAxios ): void {
    this.toastService.showError( AxiosService.getAxiosMessage( error ) );
    if ( error.response ) {
      CommonGlobalService.saveRequestToken( error.response, dispatch );
    }
    if ( onError ) {
      onError( error );
    }
    if ( [ AXIOS_STATUS.UNAUTHORIZED, AXIOS_STATUS.LICENCE_EXPIRED, AXIOS_STATUS.INVALID_TOKEN, AXIOS_STATUS.ERR_NETWORK ].includes( error.status ?? -1 )  || error.code === AXIOS_STATUS.ERR_NETWORK ) {
      CommonGlobalService.openAuthPage();
    }
  }

  static isValidGuid = ( guid: string | null | undefined ): boolean => {
    if ( guid === null || guid === undefined || typeof guid !== 'string' ) {
      return false;
    }
    const uuidRegexs = [
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i, // v4
      /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,     // v7
    ];

    return uuidRegexs.some( regex => { return regex.test( guid ); } );
  };

  //----------- CHANGE OBJECT To Boolean ---------------\\
  static toBoolean = ( value: string | number | boolean ): boolean => {
    if ( typeof value === 'boolean' ) {
      return value;
    }
    if ( typeof value === 'number' ) {
      return value === 1;
    }
    if ( typeof value === 'string' ) {
      return [ 'true', '1', 'yes', 'on' ].includes( value.toLowerCase().trim() );
    }
    return false;
  };

  static isParticipantInCommitment = ( engagement: EventCommitment, participantId: string ): boolean => {
    return engagement.participants.some( participant => {return participant.id === participantId;} );
  };

  static getEngagementOfParticipant = ( engagements: EventCommitment[], participantId: string ): EventCommitment[] => {
    return engagements.filter( engagement => {return GlobalService.isParticipantInCommitment( engagement, participantId );} );
  };

  static getEngagementsByDate = ( engagements: EventCommitment[], targetDate: dayjs.Dayjs ): EventCommitment[] => {
    const engagementsOfDate: EventCommitment[] = [];

    engagements.forEach( engagement => {
      const deliveryDate = dayjs( engagement.deliveryDate );
      if ( targetDate.isSame( deliveryDate, 'day' )  ) {
        engagementsOfDate.push( engagement );
      }
    } );

    return engagementsOfDate;
  };

  static transformEventCommitmentsToMap = ( engagements: EventCommitment[], participantsOfEngagement: User[], days: dayjs.Dayjs[] ) => {
    const participantCommitments = new Map<string, Map<string, EventCommitment[]>>;
    const allDays = Array.from( new Set( [
      ...engagements.map( item => {
        return dayjs( item.deliveryDate ).startOf( 'day' ).format( FORMAT_DATE_API );
      } ),
      ...days.map( day => { return day.startOf( 'day' ).format( FORMAT_DATE_API ); } )
    ] ) ).sort( ( date1, date2 ) => { return date1 > date2 ? 1 : -1; } );
    participantsOfEngagement.forEach( participant => {
      const engagementPerDay = new Map<string, EventCommitment[]>;
      allDays.forEach( day => {
        engagementPerDay.set( dayjs( day ).startOf( 'day' ).format( FORMAT_DATE_API ), GlobalService.getEngagementsByDate( GlobalService.getEngagementOfParticipant( engagements, participant.id ), dayjs( day ) ) );
      } );
      participantCommitments.set( participant.id, engagementPerDay );
    } );
    return participantCommitments;
  };

  static transformRangedDateToListDate = ( rangeDateGrouped: { [userId: UUID]: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs; }[]; } ): { [userId: UUID]: dayjs.Dayjs[]; } => {
    const keys = Object.keys( rangeDateGrouped );
    return keys.reduce<{ [userId: UUID]: dayjs.Dayjs[]; }>( ( result, key ) => {
      result[ key ] = rangeDateGrouped[ key ]
        .map( range => {
          const dates: Dayjs[] = [];
          let current = range.startDate;
          while ( current.isBefore( range.endDate ) || current.isSame( range.endDate ) ) {
            dates.push( current );
            current = current.add( 1, 'day' );
          }
          return dates;
        } )
        .flat();
      return result;
    }, {} );
  };

  /**
  * Construit une URL complète vers la plateforme en partant d'une route relative.
  *
  * Cette fonction :
  *  - Récupère l'URL de base de la plateforme via configService.
  *  - Normalise l'URL de base pour s'assurer qu'elle se termine par un '/'.
  *  - Encode correctement chaque segment de la route pour gérer les espaces et caractères spéciaux.
  *  - Retourne l'URL finale complète, prête à être utilisée (ex: navigation).
  *
  * @param route - La route relative après le baseUrl (ex: 'Users/123').
  * @returns Promise<string> - L'URL complète normalisée et encodée.
  */
  static async buildPlatformUrl( route: string ): Promise<string> {
    const baseUrl = await configService.getUmanforgeUrl();

    // Normalise la baseUrl pour qu'elle se termine par un slash
    const normalizedBaseUrl = baseUrl.endsWith( '/' ) ? baseUrl : `${baseUrl}/`;

    // Encode chaque segment de la route pour gérer les espaces et caractères spéciaux
    const encodedRoute = route
      .split( '/' )             // découpe la route en segments
      .map( encodeURIComponent ) // encode chaque segment
      .join( '/' );             // recompose la route

    // Crée et retourne l'URL complète
    return new URL( encodedRoute, normalizedBaseUrl ).toString();
  }
};